/**
 * wc-responsive-container
 * by pinkhominid
 * MIT Licensed
 *
 * See Philip Walton's Responsive Components: a Solution to the Container Queries Problem
 * https://philipwalton.com/articles/responsive-components-a-solution-to-the-container-queries-problem/
 *
 */

import { LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ResizeController } from '@lit-labs/observers/resize-controller.js';

const memoizedJSONParseCache = new Map<string, Record<string, number>>();

function memoizedJSONParse(jsonString: string): Record<string, number> {
  if (!memoizedJSONParseCache.has(jsonString)) {
    memoizedJSONParseCache.set(jsonString, JSON.parse(jsonString) as Record<string, number>);
  }
  const numbers = memoizedJSONParseCache.get(jsonString);
  if (numbers === undefined) {
    throw new Error('Illegal state (E091), memoized numbers not found for ' + jsonString);
  }
  return numbers;
}

const isElemOverflown = ({ clientWidth, scrollWidth }: { clientWidth: number; scrollWidth: number }) =>
  scrollWidth > clientWidth;

const updateClassList = (target: Element, removeList: string[], addList: string[]) => {
  if (removeList.length) {
    target.classList.remove(...removeList);
  }
  if (addList.length) {
    target.classList.add(...addList);
  }
  forceChildrenLayoutRecalcIfNeeded(target);
};

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const forceChildrenLayoutRecalcIfNeeded = (target: Element) => {
  if (!isSafari) return;
  // Safari needs this, unfortunately, to refresh it's styling
  Array.from(target.children).forEach((child) => {
    const childHtmlElement = child as HTMLElement;
    childHtmlElement.style.zIndex = window.getComputedStyle(childHtmlElement).zIndex;
  });
};

const DEFAULT_BREAKPOINTS = {
  width600: 600,
  width845: 845,
  width1024: 1024,
  width1220: 1220,
  width1280: 1280,
  width1600: 1600,
  width1800: 1800,
};

export class ResponsiveElement extends LitElement {
  private _resizeObserver = new ResizeController(this, {
    callback: (entries: ResizeObserverEntry[]) => {
      requestAnimationFrame(() => {
        this.handleOnResize(entries);
      });
    },
  });

  defaultBreakpoints(): { [key: string]: number } {
    return DEFAULT_BREAKPOINTS;
  }

  onResize(_contentRect: DOMRectReadOnly) {
    // DO NOTHING
  }

  private handleOnResize(entries: ResizeObserverEntry[]) {
    entries.forEach((entry) => {
      // If breakpoints are defined on the observed element,
      // use them.
      // Otherwise, use the defaults.
      const target = entry.target;
      const breaksAttr = target.getAttribute('breaks');
      const breakpoints = breaksAttr ? memoizedJSONParse(breaksAttr) : this.defaultBreakpoints();
      const breaksAddList: string[] = [];
      const breaksRemoveList: string[] = [];
      const overflownAddList: string[] = [];
      const overflownRemoveList: string[] = [];

      // Update the matching breakpoints on the observed element
      Object.entries(breakpoints).forEach(([breakpoint, minWidth]) => {
        const gteMin = entry.contentRect.width >= minWidth;
        const hasBreakClass = target.classList.contains(breakpoint);

        if (gteMin && !hasBreakClass) {
          breaksAddList.push(breakpoint);
        } else if (!gteMin && hasBreakClass) {
          breaksRemoveList.push(breakpoint);
        }
      });
      updateClassList(target, breaksRemoveList, breaksAddList);

      // mark overflown (in second pass)
      const isOverflown = isElemOverflown(target);
      const hasOverflownClass = target.classList.contains('overflown');
      if (isOverflown && !hasOverflownClass) {
        overflownAddList.push('overflown');
      } else if (!isOverflown && hasOverflownClass) {
        overflownRemoveList.push('overflown');
      }
      updateClassList(target, overflownRemoveList, overflownAddList);

      if (target instanceof ResponsiveElement) {
        target.onResize(entry.contentRect);
      }
    });
  }
}

/**
 * An element to layout containing items based on the element's size.
 *
 * The element gets classes added depending on the size (width) of the element
 */
@customElement('responsive-container')
export class ResponsiveContainer extends ResponsiveElement {
  connectedCallback() {
    super.connectedCallback();
    // Default to display block if inline
    if (getComputedStyle(this).display === 'inline') {
      this.style.display = 'block';
    }
  }

  onResize(_contentRect: DOMRectReadOnly) {
    // DO NOTHING
  }
}
